import { DEFAULT_SAVE, type GameSettings, type SaveData } from './types';

const KEY = 'aetherloom_save_v1';

export function loadSave(): SaveData {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return structuredClone(DEFAULT_SAVE);
    const parsed = JSON.parse(raw) as Partial<SaveData>;
    return {
      ...structuredClone(DEFAULT_SAVE),
      ...parsed,
      settings: { ...DEFAULT_SAVE.settings, ...(parsed.settings ?? {}) },
      levelStars: parsed.levelStars ?? {},
      levelBestScore: parsed.levelBestScore ?? {},
      levelBestMovesLeft: parsed.levelBestMovesLeft ?? {},
      chaptersCompleted: parsed.chaptersCompleted ?? [],
      tutorialsSeen: parsed.tutorialsSeen ?? [],
    };
  } catch {
    return structuredClone(DEFAULT_SAVE);
  }
}

export function writeSave(data: SaveData): void {
  try {
    localStorage.setItem(KEY, JSON.stringify(data));
  } catch {
    /* quota / private mode */
  }
}

export function updateSettings(settings: Partial<GameSettings>): SaveData {
  const save = loadSave();
  save.settings = { ...save.settings, ...settings };
  writeSave(save);
  return save;
}

export function recordLevelResult(
  levelId: number,
  stars: number,
  score: number,
  movesLeft: number,
  nextUnlock: number
): SaveData {
  const save = loadSave();
  const prev = save.levelStars[levelId] ?? 0;
  if (stars > prev) save.levelStars[levelId] = stars;
  const best = save.levelBestScore[levelId] ?? 0;
  if (score > best) save.levelBestScore[levelId] = score;
  const bestMoves = save.levelBestMovesLeft[levelId] ?? -1;
  if (movesLeft > bestMoves) save.levelBestMovesLeft[levelId] = movesLeft;
  if (nextUnlock > save.unlockedLevel) save.unlockedLevel = nextUnlock;
  save.totalScore += score;
  save.lastPlayedLevel = levelId;
  writeSave(save);
  return save;
}

export function markTutorialSeen(id: string): void {
  const save = loadSave();
  if (!save.tutorialsSeen.includes(id)) {
    save.tutorialsSeen.push(id);
    writeSave(save);
  }
}

export function resetSave(): SaveData {
  const data = structuredClone(DEFAULT_SAVE);
  writeSave(data);
  return data;
}
